"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { expansionClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * FAQs 1033 — BDU FAQs.
 *
 * Port of BDU v1 `components/organisms/FAQs.tsx`: a tinted panel with a heading and
 * gradient rule, a numbered accordion whose open row turns into a dark card, and a
 * "Contact Us" CTA that opens the lead popup.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here.
 *
 * BDU has no FAQ categories, so every category's questions are flattened into one
 * list (ordered by category then priority) and numbered 01..NN across the whole set,
 * which is what the reference does with its flat `faqs` array.
 *
 * The reference's open/close slide is driven by `animate-accordion-*` keyframes that
 * do not exist in this app, so equivalent keyframes are scoped to this widget below.
 */

interface FAQItem {
  id?: number;
  question?: string;
  question_text_color?: string;
  answer?: string;
  answer_text_color?: string;
  priority?: number;
}

interface Category {
  id?: number;
  order_index?: number;
  status?: boolean;
  faqs?: FAQItem[];
}

interface Faqs1033Data {
  categories?: Category[];
  title?: string;
  title_text_color?: string;
  section_background_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Open row surface + its text */
  background_color?: string;
  section_color?: string;
  /** Muted index number on closed rows */
  accordion_muted_index_color?: string;
  /** Answer panel */
  accordion_answer_surface_bg?: string;
  accordion_answer_surface_border?: string;
  /** CTA gradient — the label itself is fixed copy, as in the reference */
  gradient_color_3?: string;
  gradient_color_4?: string;
  status?: boolean;
}

/** Fixed in the reference (not a CMS value), so it stays fixed here. */
const CTA_LABEL = "Contact Us";

const Faqs1033 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Faqs1033Data | undefined;
  const dispatch = useDispatch();
  const [openItem, setOpenItem] = useState<string>("item-0");

  const {
    title = "FAQs",
    title_text_color = "#000000",
    section_background_color = "#F7F7F7",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    background_color = "#173551",
    section_color = "#FFFFFF",
    accordion_muted_index_color = "#3C3C4380",
    accordion_answer_surface_bg = "#ECF6FF",
    accordion_answer_surface_border = "#AABFD3",
    gradient_color_3 = "#FF0013",
    gradient_color_4 = "#990013",
  } = sectionData || {};

  const faqs = useMemo(() => {
    const cats = sectionData?.categories;
    if (!Array.isArray(cats)) return [];
    return cats
      .filter((c) => c?.status !== false)
      .sort(
        (a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0),
      )
      .flatMap((c) =>
        [...(c.faqs || [])].sort(
          (a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0),
        ),
      );
  }, [sectionData?.categories]);

  if (!sectionData?.status || faqs.length === 0) return null;

  return (
    <section
      className="relative w-full px-[19px] py-[48px] tab:px-[64px]"
      style={{ backgroundColor: section_background_color }}
    >
      <style>{`
        @keyframes bduFaqDown { from { height: 0 } to { height: var(--radix-accordion-content-height) } }
        @keyframes bduFaqUp { from { height: var(--radix-accordion-content-height) } to { height: 0 } }
        [data-bdu-faq][data-state="open"] { animation: bduFaqDown 200ms ease-out; }
        [data-bdu-faq][data-state="closed"] { animation: bduFaqUp 200ms ease-out; }
      `}</style>

      <div className="flex items-start justify-between">
        <div className="w-full space-y-[24px] tab:space-y-[39px]">
          <div className="flex flex-col gap-[8px] tab:gap-[12px]">
            <h2
              className="text-[40px] font-bold tab:text-[28px]"
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
            <div
              className="h-[3px] w-[102px]"
              style={{
                backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
              }}
            />
          </div>

          <AccordionPrimitive.Root
            type="single"
            collapsible
            value={openItem}
            onValueChange={setOpenItem}
            className="flex flex-col gap-2"
          >
            {faqs.map((faq, index) => {
              const value = `item-${index}`;
              const isOpen = openItem === value;

              return (
                <AccordionPrimitive.Item
                  key={faq.id ?? index}
                  value={value}
                  className="border-b border-[#EFEFEF]"
                >
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger
                      onClick={() =>
                        expansionClick({
                          section: "faqs_1033",
                          component: "icon_expansion",
                          label: faq.question || "",
                          entity_name: "faq",
                          parent_entity: "faqs_1033",
                        })
                      }
                      className={cn(
                        "flex flex-1 items-center justify-between py-4 text-left font-semibold transition-all",
                        "px-[12px] text-[16px]/[19px]",
                        isOpen
                          ? // 13px at EVERY width — the open row is deliberately not
                            // inset to 60px like the closed ones. In the reference this
                            // falls out of specificity, not intent-per-breakpoint:
                            // `data-[state=open]:px-[13px]` compiles to
                            // `.…\:px-\[13px\][data-state=open]` (0,2,0) and beats the
                            // plain `.md\:px-\[60px\]` (0,1,0) even inside its media
                            // query. Emitting `tab:px-[60px]` here would be equal
                            // specificity, win on source order, and re-inset the row.
                            "rounded-t-lg px-[13px]"
                          : "items-start rounded-lg tab:items-center tab:px-[60px]",
                      )}
                      style={{
                        boxShadow: "0px 4px 4.5px rgba(0, 0, 0, 0.08)",
                        backgroundColor: isOpen ? background_color : undefined,
                        color: isOpen ? section_color : "#000000",
                      }}
                    >
                      <div className="flex w-full items-start justify-between gap-3 tab:items-center">
                        <span
                          className={cn(
                            "font-bold",
                            isOpen
                              ? "text-[32px]/[38px]"
                              : "text-[20px]/[24px] tab:text-[32px]/[38px]",
                          )}
                          style={{
                            color: isOpen
                              ? section_color
                              : accordion_muted_index_color,
                          }}
                        >
                          {index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </span>
                        <p className="flex-1 text-[14px] font-semibold tab:text-[16px]">
                          {faq.question}
                        </p>
                        {isOpen ? (
                          <MinusIcon
                            className="size-7 shrink-0 rounded-full text-[#000000]"
                            style={{ backgroundColor: section_color }}
                          />
                        ) : (
                          <PlusIcon
                            className="size-7 shrink-0 rounded-full text-[#000000]"
                            style={{ backgroundColor: section_color }}
                          />
                        )}
                      </div>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>

                  <AccordionPrimitive.Content
                    data-bdu-faq
                    className="mt-0 overflow-hidden pb-0 text-[14px] font-normal"
                    style={{ color: faq.answer_text_color || "#4F4F4F" }}
                  >
                    <div
                      className={cn(
                        "rounded-b-lg border border-solid py-6 pl-[60px]",
                        "tab:px-[60px] tab:py-5",
                      )}
                      style={{
                        backgroundColor: accordion_answer_surface_bg,
                        borderColor: accordion_answer_surface_border,
                      }}
                    >
                      {faq.answer}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              );
            })}
          </AccordionPrimitive.Root>

          <button
            type="button"
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }),
              )
            }
            className="flex h-[48px] w-full items-center justify-center gap-[10px] rounded-md px-[64px] py-[12px] text-[20px] font-light text-white shadow tab:h-[56px] tab:w-auto"
            style={{
              backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
            }}
          >
            {CTA_LABEL}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Faqs1033;
